/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useState, useMemo, useEffect, useId } from 'react';
import Tabs from '@/components/Tabs/Tabs';
import Tab from '@/components/Tab/Tab';
import {
  CertonaBlock,
  type CertonaBlockProps,
  type CertonaRecommendation,
} from '@/features/certona';
import { ScrolledPartCardSkeleton } from '@/features/certona/components/ScrolledPartCardSkeleton';
import type { ContentStackCertonaTabbed } from '../../interface';
import { useResponsiveCertonaContainerStyle } from '../../hooks/useResponsiveCertonaContainerStyle';
import { extractTabNumber } from '@/features/certona/utils/extractTabNumber';
import styles from './CMSCertonaTabbed.module.scss';

const TabbedCertonaSkeleton = () => <ScrolledPartCardSkeleton hideTitle={true} />;

const hasRecommendationsForTab = (
  containerId: string,
  certonaRecommendations?: CertonaRecommendation[],
): boolean => {
  if (!certonaRecommendations?.length) {
    return false;
  }

  const index = certonaRecommendations.findIndex((rec) => rec.scheme === containerId);

  if (index === -1) {
    return false;
  }

  const recommendation = certonaRecommendations[index];
  return !!(recommendation?.display && recommendation?.items?.length > 0);
};

type Props = {
  content: ContentStackCertonaTabbed;
} & CertonaBlockProps;

type Theme = 'white' | 'dark';
type CertonaTheme = 'white' | 'grey';

const responsiveTargets = { tablet: true, desktop: true, mobile: true };

export function CMSCertonaTabbed({
  content,
  certonaRecommendations,
  LoadingSkeleton: _unusedLoadingSkeleton,
  ...certonaProps
}: Props) {
  const baseId = useId();
  const { tabs, theme, subtitle, container_style } = content;
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const currentTheme = theme as Theme;
  const customContainerStyle = useResponsiveCertonaContainerStyle(container_style);

  const visibleTabs = useMemo(() => {
    if (!tabs?.length) {
      return [];
    }

    return tabs.filter((tab) => hasRecommendationsForTab(tab.container_id, certonaRecommendations));
  }, [tabs, certonaRecommendations]);

  useEffect(() => {
    if (visibleTabs.length > 0 && activeTabIndex >= visibleTabs.length) {
      setActiveTabIndex(0);
    }
  }, [visibleTabs.length, activeTabIndex]);

  if (visibleTabs.length === 0) {
    return null;
  }

  const mapThemeToCertonaTheme = (theme: Theme): CertonaTheme =>
    theme === 'dark' ? 'grey' : 'white';

  const handleTabChange = (_event: React.SyntheticEvent<HTMLButtonElement>, newIndex: number) => {
    setActiveTabIndex(newIndex);
  };

  // Ensure activeTabIndex is within bounds
  const currentActiveTabIndex = Math.min(activeTabIndex, visibleTabs.length - 1);
  const activeTab = visibleTabs[currentActiveTabIndex];
  const recTabNumber = extractTabNumber(activeTab?.container_id);

  const tabsId = `${baseId}-tabs`;
  const tabPanelId = `${baseId}-tabpanel`;

  const contentItem = {
    name: '', // Don't pass title to avoid duplicate headers
    containerID: activeTab.container_id,
    themeCode: mapThemeToCertonaTheme(currentTheme),
    ...responsiveTargets,
  };

  return (
    <div className={styles.container}>
      {subtitle && <h2 className={styles.sectionTitle}>{subtitle}</h2>}
      <Tabs
        value={currentActiveTabIndex}
        onChange={handleTabChange}
        hideIndicator={true}
        classes={{
          flexContainer: styles.tabsContainer,
        }}
        aria-label={subtitle || 'Top rated for you'}
      >
        {visibleTabs.map((tab, index) => {
          const tabId = `${tabsId}-tab-${index}`;

          return (
            <Tab
              id={tabId}
              className={styles.tab}
              key={tab.container_id}
              classes={{
                selected: styles.selected,
              }}
              label={tab.subtitle}
              value={index}
              aria-controls={`${tabPanelId}-${index}`}
              tabIndex={index === currentActiveTabIndex ? 0 : -1}
              selectionFollowsFocus={true}
            />
          );
        })}
      </Tabs>

      <div
        className={styles.tabContent}
        role="tabpanel"
        id={`${tabPanelId}-${currentActiveTabIndex}`}
        aria-labelledby={`${tabsId}-tab-${currentActiveTabIndex}`}
        tabIndex={0}
      >
        <CertonaBlock
          contentItem={contentItem}
          certonaRecommendations={certonaRecommendations}
          containerType={customContainerStyle}
          LoadingSkeleton={TabbedCertonaSkeleton}
          recTabNumber={recTabNumber}
          {...certonaProps}
        />
      </div>
    </div>
  );
}
